/**
 * Terug van Mollie (SPEC §4.2 stap 5). Vraagt de stand op en blijft dat even
 * doen zolang de betaling nog niet beslist is.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, type Bedanktstand } from '../api';
import { Knop, Kroon, Laden, Melding, Niveaubadge, useTitel } from '../componenten/basis';
import { Pagina } from '../componenten/Schil';

export function Bedankt() {
  useTitel('Bedankt');
  const [params] = useSearchParams();
  const token = params.get('held');
  const [stand, zetStand] = useState<Bedanktstand | null>(null);
  const [fout, zetFout] = useState<string | null>(null);
  const [pogingen, zetPogingen] = useState(0);

  useEffect(() => {
    if (!token) return;
    let gestopt = false;
    api
      .haal<Bedanktstand>(`/api/bedankt/${encodeURIComponent(token)}`)
      .then((s) => {
        if (gestopt) return;
        zetStand(s);
        if (s.status === 'wacht' && pogingen < 10) {
          setTimeout(() => zetPogingen((p) => p + 1), 2500);
        }
      })
      .catch((f: Error) => zetFout(f.message));
    return () => {
      gestopt = true;
    };
  }, [token, pogingen]);

  if (!token) {
    return (
      <Pagina smal>
        <div className="py-20 text-center">
          <h1 className="text-3xl">Hier is niets te zien.</h1>
          <Knop naar="/" soort="stil" className="mt-6">
            Naar de muur
          </Knop>
        </div>
      </Pagina>
    );
  }

  return (
    <Pagina smal>
      <div className="verschijn py-16 text-center sm:py-24">
        {fout && <Melding soort="fout">{fout}</Melding>}
        {!stand && !fout && <Laden tekst="We kijken even bij de bank…" />}
        {stand?.status === 'wacht' && (
          <>
            <Kroon className="mx-auto h-14 w-14 animate-pulse text-goud-600" />
            <h1 className="mt-6 text-4xl">Nog even…</h1>
            <p className="mt-3 text-inkt-zacht">
              De bank heeft de betaling nog niet bevestigd. Dat duurt meestal een paar seconden. Deze pagina ververst vanzelf.
            </p>
            {pogingen >= 10 && (
              <p className="mt-6 text-sm text-inkt-zacht">
                Duurt het langer? Dan verwerken we de betaling zodra de bank hem doorgeeft, en krijg je vanzelf een mail.
              </p>
            )}
          </>
        )}
        {stand?.status === 'betaald' && (
          <>
            <Kroon className="mx-auto h-16 w-16 text-goud-400 drop-shadow-[0_0_24px_rgba(233,194,90,0.5)]" />
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.3em] text-goud-500">Welkom op de muur</p>
            <h1 className="mt-3 text-5xl">
              Dank je wel, <span className="text-goud-300">{stand.weergavenaam}</span>.
            </h1>
            <p className="mt-4 text-inkt-zacht">
              Je bijdrage is binnen en je staat vanaf nu op de muur. Er staat een welkomstmail voor je klaar met alles wat je moet weten.
            </p>
            {stand.niveau && (
              <p className="mt-6">
                <Niveaubadge niveau={stand.niveau} groot />
              </p>
            )}
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Knop naar="/#muur">Bekijk de muur</Knop>
              <Knop naar="/mijn" soort="stil">
                Naar mijn pagina
              </Knop>
            </div>
          </>
        )}
        {stand?.status === 'mislukt' && (
          <>
            <Kroon className="mx-auto h-14 w-14 text-inkt-stil" />
            <h1 className="mt-6 text-4xl">De betaling is niet gelukt.</h1>
            <p className="mt-3 text-inkt-zacht">
              Geen zorgen: er is niets afgeschreven en je staat nog nergens. Je kunt het gewoon opnieuw proberen met hetzelfde e-mailadres.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Knop naar="/word-held">Opnieuw proberen</Knop>
              <Link to="/" className="knop knop-stil">
                Naar de muur
              </Link>
            </div>
          </>
        )}
      </div>
    </Pagina>
  );
}
